import React, { useEffect, useMemo, useRef, useState } from 'react';
import { trackZoomFactor } from '../trackZoom';
import './LossesTrack.css';

const LABEL_COL_PX = 100;
const MIN_INTERVAL_SEC = 1;
const MAX_INTERVAL_SEC = 60;
const DEFAULT_INTERVAL_SEC = 5;

/** @typedef {'window' | 'battles'} AggregateMode */

function formatClock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function buildAxisTicks(maxT, trackWidthPx) {
  if (maxT <= 0 || trackWidthPx <= 0) return [0, maxT];
  const targetPx = 80;
  const roughStep = (maxT * targetPx) / trackWidthPx;
  const niceSteps = [5, 10, 15, 30, 60, 120, 180, 300, 600];
  const step = niceSteps.find((s) => s >= roughStep) || Math.ceil(roughStep / 60) * 60;
  const ticks = [];
  for (let t = 0; t <= maxT + 0.001; t += step) {
    ticks.push(Math.min(maxT, t));
  }
  if (ticks[ticks.length - 1] < maxT - 0.5) ticks.push(maxT);
  return ticks;
}

function formatBucketLabel(items, maxParts = 2) {
  if (!items || items.length === 0) return '';
  const parts = items.slice(0, maxParts).map((i) => {
    const short = String(i.name || '?').slice(0, 3);
    return i.count > 1 ? `${short}×${i.count}` : short;
  });
  if (items.length > maxParts) parts.push('…');
  return parts.join(' ');
}

function formatBucketTooltip(bucket) {
  if (!bucket) return '';
  const comps = (bucket.items || [])
    .map((i) => (i.count > 1 ? `${i.name}×${i.count}` : i.name))
    .join(', ');
  return `${comps} · ${bucket.minerals}/${bucket.gas} (Σ ${bucket.total})`;
}

/**
 * Losses Track — lanes from /api/replay_facts/…/losses_track.
 * Mode and ±1s re-request; no local aggregation (D25/D26).
 */
const LossesTrack = ({
  apiBaseUrl = null,
  replayId = null,
  playerColors = {},
  duration = 0,
  time = 0,
  onSeek,
  zoomIndex = 0,
  onHoverChange,
  scrollLeft = 0,
  onScrollLeftChange,
}) => {
  const scrollRef = useRef(null);
  const onSeekRef = useRef(onSeek);
  onSeekRef.current = onSeek;
  const onHoverRef = useRef(onHoverChange);
  onHoverRef.current = onHoverChange;
  const [viewportWidth, setViewportWidth] = useState(0);
  const [intervalSec, setIntervalSec] = useState(DEFAULT_INTERVAL_SEC);
  /** @type {[AggregateMode, function]} */
  const [mode, setMode] = useState('window');
  const [lanesState, setLanesState] = useState({
    status: 'idle',
    lanes: [],
    error: '',
  });

  useEffect(() => {
    if (!apiBaseUrl || !replayId) {
      setLanesState({ status: 'empty', lanes: [], error: '' });
      return undefined;
    }
    let cancelled = false;
    setLanesState((prev) => ({ ...prev, status: 'loading', error: '' }));
    const url =
      `${apiBaseUrl}/replay_facts/${encodeURIComponent(replayId)}` +
      `/losses_track?mode=${encodeURIComponent(mode)}&interval=${intervalSec}`;
    fetch(url)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        const lanes = Array.isArray(data?.lanes) ? data.lanes : [];
        setLanesState({
          status: lanes.length === 0 ? 'empty' : 'ready',
          lanes,
          error: '',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setLanesState({
          status: 'error',
          lanes: [],
          error: err?.message || 'Request failed',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, replayId, mode, intervalSec]);

  const maxT = Math.max(
    duration || 0,
    ...lanesState.lanes.flatMap((lane) =>
      (lane.buckets || []).map((b) => Math.max(b.end || 0, b.start || 0))
    ),
    1
  );

  const rows = useMemo(
    () =>
      (lanesState.lanes || []).map((lane) => ({
        id: lane.player || `pid-${lane.pid}`,
        label: lane.player || 'Player',
        color: playerColors[lane.player] || '#95a5a6',
        buckets: lane.buckets || [],
      })),
    [lanesState.lanes, playerColors]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setViewportWidth(el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rows, mode]);

  const timelineViewport = Math.max(0, viewportWidth - LABEL_COL_PX - 8);
  const fitPxPerSecond = timelineViewport > 0 ? timelineViewport / maxT : 1;
  const zoom = trackZoomFactor(zoomIndex);
  const timelineWidthPx = Math.max(timelineViewport, maxT * fitPxPerSecond * zoom, 1);
  const innerWidthPx = LABEL_COL_PX + 8 + timelineWidthPx;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || scrollLeft == null) return;
    if (Math.abs(el.scrollLeft - scrollLeft) > 1) {
      el.scrollLeft = scrollLeft;
    }
  }, [scrollLeft, timelineWidthPx]);

  const axisTicks = useMemo(
    () => buildAxisTicks(maxT, timelineWidthPx),
    [maxT, timelineWidthPx]
  );

  const handleTrackClick = (event) => {
    if (!onSeekRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = event.clientX - rect.left + el.scrollLeft - LABEL_COL_PX - 8;
    if (x < 0) return;
    const ratio = Math.min(1, Math.max(0, x / timelineWidthPx));
    onSeekRef.current(ratio * maxT);
  };

  const playheadLeft = LABEL_COL_PX + 8 + (time / maxT) * timelineWidthPx;
  const intervalHint = mode === 'battles' ? 'battle gap' : 'window';

  let body;
  if (lanesState.status === 'loading' || lanesState.status === 'idle') {
    body = <p className="losses-track-empty">Loading Losses Track…</p>;
  } else if (lanesState.status === 'error') {
    body = (
      <p className="losses-track-empty" data-testid="losses-track-error">
        Could not load Losses Track ({lanesState.error}).
      </p>
    );
  } else if (lanesState.status === 'empty') {
    body = <p className="losses-track-empty">No losses in timelines (or missing parse).</p>;
  } else {
    body = (
      <div
        className="losses-track-scroll"
        ref={scrollRef}
        data-testid="losses-track-scroll"
        onScroll={(e) => onScrollLeftChange?.(e.currentTarget.scrollLeft)}
      >
        <div className="losses-track-inner" style={{ width: `${innerWidthPx}px` }}>
          <div
            className="losses-track-axis"
            style={{ marginLeft: `${LABEL_COL_PX + 8}px` }}
          >
            {axisTicks.map((t) => (
              <span
                key={t}
                className="losses-axis-tick"
                style={{ left: `${(t / maxT) * 100}%` }}
              >
                {formatClock(t)}
              </span>
            ))}
          </div>
          <div
            className="losses-track-lanes"
            onClick={handleTrackClick}
            role="presentation"
            aria-label="Losses track"
          >
            {rows.map((row) => (
              <div
                key={row.id}
                className="losses-row"
                style={{ gridTemplateColumns: `${LABEL_COL_PX}px 1fr` }}
                data-testid={`losses-row-${row.id}`}
              >
                <div
                  className="losses-row-label"
                  title={row.label}
                  style={{ color: row.color }}
                >
                  {row.label}
                </div>
                <div className="losses-lane" style={{ width: `${timelineWidthPx}px` }}>
                  {row.buckets.map((bucket) => {
                    const left = (bucket.start / maxT) * timelineWidthPx;
                    const span = Math.max(bucket.end - bucket.start, 0.5);
                    const width = Math.max(8, (span / maxT) * timelineWidthPx);
                    const tip = formatBucketTooltip(bucket);
                    const label =
                      mode === 'battles'
                        ? formatBucketLabel(bucket.items, 3)
                        : String(bucket.total);
                    return (
                      <button
                        key={`${row.id}-${bucket.start}-${bucket.end}`}
                        type="button"
                        className={`losses-cell${mode === 'battles' ? ' battle' : ''}`}
                        style={{
                          left: `${left}px`,
                          width: `${width}px`,
                          borderColor: row.color,
                        }}
                        aria-label={tip}
                        onMouseEnter={(e) => {
                          onHoverRef.current?.({
                            kind: 'loss',
                            text: tip,
                            bucket,
                            playerName: row.label,
                            clientX: e.clientX,
                            clientY: e.clientY,
                          });
                        }}
                        onMouseMove={(e) => {
                          onHoverRef.current?.({
                            kind: 'loss',
                            text: tip,
                            bucket,
                            playerName: row.label,
                            clientX: e.clientX,
                            clientY: e.clientY,
                          });
                        }}
                        onMouseLeave={() => onHoverRef.current?.(null)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSeekRef.current?.(bucket.start);
                        }}
                      >
                        <span className="losses-cell-label">{label}</span>
                        <span className="losses-cell-cost">
                          {bucket.minerals}/{bucket.gas}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div
              className="losses-playhead"
              style={{ left: `${playheadLeft}px` }}
              aria-hidden
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <details className="losses-track" open data-testid="losses-track">
      <summary className="losses-track-summary">
        <span>Losses Track</span>
        <span className="losses-track-summary-meta">
          all players{zoom > 1 ? ` · ${zoom}×` : ''}
        </span>
      </summary>
      <div className="losses-track-body">
        <div className="losses-track-controls">
          <div className="losses-track-interval" role="group" aria-label="Loss aggregation interval">
            <button
              type="button"
              className="losses-track-btn"
              aria-label="Decrease interval"
              disabled={intervalSec <= MIN_INTERVAL_SEC}
              onClick={() => setIntervalSec((s) => Math.max(MIN_INTERVAL_SEC, s - 1))}
            >
              &lt;&gt;
            </button>
            <span className="losses-track-interval-label" data-testid="losses-bucket-sec">
              {intervalSec}s
            </span>
            <button
              type="button"
              className="losses-track-btn"
              aria-label="Increase interval"
              disabled={intervalSec >= MAX_INTERVAL_SEC}
              onClick={() => setIntervalSec((s) => Math.min(MAX_INTERVAL_SEC, s + 1))}
            >
              &gt;&lt;
            </button>
            <span className="losses-track-interval-hint">{intervalHint}</span>
          </div>
          <div
            className="losses-mode-toggle"
            role="group"
            aria-label="Loss aggregation mode"
            data-testid="losses-mode-toggle"
          >
            <button
              type="button"
              className={`losses-track-btn${mode === 'window' ? ' active' : ''}`}
              aria-pressed={mode === 'window'}
              data-testid="losses-mode-window"
              onClick={() => setMode('window')}
            >
              Sum in window
            </button>
            <button
              type="button"
              className={`losses-track-btn${mode === 'battles' ? ' active' : ''}`}
              aria-pressed={mode === 'battles'}
              data-testid="losses-mode-battles"
              onClick={() => setMode('battles')}
            >
              Group battles
            </button>
          </div>
        </div>
        {body}
      </div>
    </details>
  );
};

export default LossesTrack;
